import React from 'react';
import { Link } from 'react-router-dom';
import ZitoFooter from '../components/ZitoFooter';

/**
 * /about — Zitotron Tech LLC company anchor page.
 *
 * Mirrors AgentOmatic.io/about in structure: company identity, legal
 * doc index, three-email contact split, red/green commercial posture,
 * security disclosure. This is the "prove you're a real company" page.
 */

const AboutPage: React.FC = () => {
  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0f', color: '#cfcfdc' }}>
      <div style={{ maxWidth: 860, margin: '0 auto', padding: '56px 24px 48px' }}>

        {/* Back link */}
        <div style={{ marginBottom: 20 }}>
          <Link to="/" style={{ color: '#6a6a78', textDecoration: 'none', fontSize: 12, fontWeight: 600 }}>
            ← Home
          </Link>
        </div>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: '2.6rem', marginBottom: 6 }}>⚡</div>
          <h1 style={{
            margin: '0 0 8px', fontSize: 'clamp(2rem, 5vw, 2.6rem)', fontWeight: 800,
            background: 'linear-gradient(135deg, #a78bfa 0%, #2dd4bf 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
            filter: 'drop-shadow(0 0 10px rgba(167,139,250,0.35))',
            letterSpacing: -0.5,
          }}>
            Zitotron Tech LLC
          </h1>
          <p style={{ margin: 0, fontSize: 13, color: '#9a9ab0' }}>
            Portland, Oregon, USA 🇺🇸 · Software company · Proprietary commercial software
          </p>
        </div>

        {/* Company */}
        <section style={card}>
          <h2 style={h2}>Who we are</h2>
          <p style={p}>
            Zitotron Tech LLC is a Portland, Oregon-based software company building a portfolio
            of proprietary commercial software products, including <a href="https://agentomatic.io" style={accentLink}>AgentOmatic.io</a>,
            <a href="https://vidaforge.ai" style={accentLink}>VidaForge.ai</a>, and <a href="https://postavo.ai" style={accentLink}>Postavo.ai</a>. The portfolio spans AI coding-agent orchestration,
            gamified life execution, and agent-assisted social publishing systems.
          </p>
          <p style={p}>
            We ship proprietary commercial software: a free Community tier lets anyone use the full product at personal scale, while paid tiers and custom commercial
            customers (OEM, white-label, closed-source SaaS) sign a separate commercial license.
            Custom engagements, consulting, and enterprise deployments go through our sales team.
          </p>
        </section>

        {/* Build / version info — signals "real company" per the brief */}
        <section style={card}>
          <h2 style={h2}>Facts</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
            <Tile label="Company"     value="Zitotron Tech LLC" />
            <Tile label="Form"        value="Limited Liability Company (USA)" />
            <Tile label="Formed"      value="2024" />
            <Tile label="HQ"          value="Portland, Oregon, USA" />
            <Tile label="Year"        value="© 2026" />
            <Tile label="Flagship"    value={<a href="https://agentomatic.io" style={accentLink}>AgentOmatic.io →</a>} />
            <Tile label="License"     value="Proprietary commercial" />
            <Tile label="Source"      value={<a href="https://github.com/ZitotronTechLLC" target="_blank" rel="noopener noreferrer" style={accentLink}>GitHub →</a>} />
            <Tile label="X (Twitter)" value={<a href="https://x.com/ZitotronTechLLC" target="_blank" rel="noopener noreferrer" style={accentLink}>@ZitotronTechLLC →</a>} />
          </div>
        </section>

        {/* Commercial posture — red */}
        <section style={{
          ...card,
          background: 'rgba(248,113,113,0.06)',
          border: '1px solid rgba(248,113,113,0.35)',
        }}>
          <div style={{
            fontSize: 10, letterSpacing: 2, textTransform: 'uppercase',
            color: '#f87171', fontWeight: 800, marginBottom: 8,
            textShadow: '0 0 6px rgba(248,113,113,0.45)',
          }}>⚠ Commercial cloning notice</div>
          <p style={p}>
            If you clone, fork, white-label, repackage, or otherwise commercialize any Zitotron
            product in violation of its License (e.g. unauthorized redistribution, reverse-engineering, closed-source white-label without
            release), <strong>Zitotron Tech LLC will pursue all available legal remedies.</strong>
          </p>
          <p style={{ ...p, marginBottom: 0 }}>
            License enforcement / IP / takedown:{' '}
            <a href="mailto:legal@zitotrontech.com?subject=License%20Violation%20Report"
               style={{ color: '#f87171', fontWeight: 700 }}>legal@zitotrontech.com</a>
          </p>
        </section>

        {/* Commercial posture — green */}
        <section style={{
          ...card,
          background: 'rgba(34,197,94,0.06)',
          border: '1px solid rgba(34,197,94,0.40)',
          boxShadow: '0 0 22px rgba(34,197,94,0.15)',
        }}>
          <div style={{
            fontSize: 10, letterSpacing: 2, textTransform: 'uppercase',
            color: '#4ade80', fontWeight: 800, marginBottom: 8,
            textShadow: '0 0 6px rgba(74,222,128,0.55)',
          }}>💼 Building something commercial?</div>
          <p style={p}>
            We dual-license for closed-source, OEM, white-label, and enterprise integrations.
            Custom contracts, volume deals, partnerships, and acquisition inquiries all go through
            our sales team.
          </p>
          <a href="mailto:sales@zitotrontech.com?subject=Commercial%20Inquiry"
             style={{
               display: 'inline-flex', alignItems: 'center', gap: 8,
               padding: '11px 20px', borderRadius: 10,
               background: 'linear-gradient(135deg, #22c55e 0%, #4ade80 100%)',
               color: '#05170c', fontWeight: 800, fontSize: 13, textDecoration: 'none',
               boxShadow: '0 0 18px rgba(34,197,94,0.50)', marginTop: 6,
             }}>✉ sales@zitotrontech.com</a>
          <p style={{ margin: '10px 0 0', fontSize: 11, color: '#4ade80', fontStyle: 'italic' }}>
            Friendly, fast, real humans.
          </p>
        </section>

        {/* Legal docs index — cross-linked to AgentOmatic.io/legal/* */}
        <section style={card}>
          <h2 style={h2}>Legal</h2>
          <p style={p}>
            The authoritative legal docs are rendered on{' '}
            <a href="https://agentomatic.io" style={accentLink}>AgentOmatic.io</a>. Zitotron Tech
            LLC is the legal entity behind both properties — the same docs apply.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, marginTop: 12 }}>
            <LegalLink href="https://agentomatic.io/legal/license"    label="License"          desc="Full commercial license text" />
            <LegalLink href="https://agentomatic.io/legal/eula"       label="EULA"             desc="End-User License Agreement" />
            <LegalLink href="https://agentomatic.io/legal/commercial" label="Commercial License" desc="Closed-source / OEM paths" />
            <LegalLink href="https://agentomatic.io/legal/trademark"  label="Trademark Policy" desc="Name + logo usage rules" />
            <LegalLink href="https://agentomatic.io/legal/privacy"    label="Privacy Policy"   desc="Data collection + your rights" />
            <LegalLink href="https://agentomatic.io/legal/notices"    label="Third-Party Notices" desc="Bundled OSS component notices" />
          </div>
        </section>

        {/* Security */}
        <section style={{ ...card, background: 'rgba(34,211,238,0.04)', border: '1px solid rgba(34,211,238,0.30)' }}>
          <div style={{
            fontSize: 10, letterSpacing: 2, textTransform: 'uppercase',
            color: '#22d3ee', fontWeight: 800, marginBottom: 8,
            textShadow: '0 0 6px rgba(34,211,238,0.55)',
          }}>🛡️ Security disclosure</div>
          <p style={{ ...p, marginBottom: 0 }}>
            Report security vulnerabilities to{' '}
            <a href="mailto:support@zitotrontech.com?subject=%5BSECURITY%5D%20Vulnerability%20disclosure"
               style={{ color: '#22d3ee', fontWeight: 700 }}>
              support@zitotrontech.com
            </a>{' '}
            with subject line <code style={codeStyle}>[SECURITY]</code>. We acknowledge within 5 business days.
            Please allow reasonable time for investigation and patching before public disclosure.
          </p>
        </section>

        {/* Three-email contact split */}
        <section style={card}>
          <h2 style={h2}>Reach us</h2>
          <p style={p}>
            Three staffed inboxes. No generic <code style={codeStyle}>info@</code> or <code style={codeStyle}>hello@</code> routing.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10, marginTop: 10 }}>
            <ContactCard color="#67e8f9" email="support@zitotrontech.com" label="Support"
              desc="Bugs · help · product questions · account issues · security [SECURITY]" />
            <ContactCard color="#4ade80" email="sales@zitotrontech.com" label="Sales"
              desc="Licensing · OEM · enterprise · custom · partnerships · acquisition"
              italic="Friendly, fast, real humans." />
            <ContactCard color="#f87171" email="legal@zitotrontech.com" label="Legal"
              desc="License enforcement · IP · takedown · GDPR / CCPA data requests" />
          </div>
        </section>

      </div>
      <ZitoFooter />
    </div>
  );
};

// ─── Sub-components + styles ────────────────────────────────────────────────

const Tile: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div style={{ padding: '10px 14px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10 }}>
    <div style={{ fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', color: '#9a9ab0', fontWeight: 700, marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 13, fontWeight: 600, color: '#cfcfdc' }}>{value}</div>
  </div>
);

const LegalLink: React.FC<{ href: string; label: string; desc: string }> = ({ href, label, desc }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" style={{
    display: 'block', padding: '10px 14px',
    background: 'rgba(167,139,250,0.04)',
    border: '1px solid rgba(167,139,250,0.22)',
    borderRadius: 10, textDecoration: 'none',
  }}>
    <div style={{ fontSize: 13, fontWeight: 700, color: '#a78bfa', textShadow: '0 0 6px rgba(167,139,250,0.40)' }}>{label} ↗</div>
    <div style={{ fontSize: 11, color: '#9a9ab0', marginTop: 2 }}>{desc}</div>
  </a>
);

const ContactCard: React.FC<{ color: string; email: string; label: string; desc: string; italic?: string }> = ({ color, email, label, desc, italic }) => (
  <div style={{ padding: '12px 14px', background: `${color}0F`, border: `1px solid ${color}55`, borderRadius: 10 }}>
    <div style={{ fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase', color, fontWeight: 800, marginBottom: 4, textShadow: `0 0 6px ${color}55` }}>{label}</div>
    <a href={`mailto:${email}`} style={{ color, textDecoration: 'none', fontWeight: 700, fontSize: 13, textShadow: `0 0 6px ${color}55`, display: 'block', marginBottom: 4 }}>{email}</a>
    <p style={{ margin: 0, fontSize: 11, color: '#9a9ab0', lineHeight: 1.5 }}>{desc}</p>
    {italic && <p style={{ margin: '6px 0 0', fontSize: 11, color, fontStyle: 'italic' }}>{italic}</p>}
  </div>
);

const card: React.CSSProperties = {
  padding: '22px 26px', marginBottom: 18,
  background: 'rgba(255,255,255,0.015)',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 14,
};
const h2: React.CSSProperties = { margin: '0 0 12px', fontSize: '1.05rem', fontWeight: 700, color: '#cfcfdc' };
const p: React.CSSProperties = { margin: '0 0 10px', fontSize: 14, color: '#9a9ab0', lineHeight: 1.65 };
const accentLink: React.CSSProperties = { color: '#a78bfa', textDecoration: 'underline', textUnderlineOffset: 2 };
const codeStyle: React.CSSProperties = {
  fontFamily: 'monospace', fontSize: '0.88em', padding: '1px 6px',
  background: 'rgba(167,139,250,0.12)', border: '1px solid rgba(167,139,250,0.25)', borderRadius: 4,
};

export default AboutPage;
